"use client";
import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import { GlobalStyles } from "@mui/joy";
import CssBaseline from "@mui/joy/CssBaseline";
import { CssVarsProvider } from "@mui/joy/styles";
import { useServerInsertedHTML } from "next/navigation";
import React from "react";
import { useActiveExperience } from "@/lib/features/experiences/hooks";
import { useThemePreferenceSync } from "@/src/hooks/themePreferenceHooks";
import modernTheme from "@/lib/features/experiences/modern/theme";
import classicTheme from "@/lib/features/experiences/classic/theme";

// This implementation is from emotion-js
// https://github.com/emotion-js/emotion/issues/2928#issuecomment-1319747902
export default function ThemeRegistry(
  props: React.PropsWithChildren<{ options?: any }>
) {
  const { options, children } = props;

  const [{ cache, flush }] = React.useState(() => {
    const cache = createCache(options);
    cache.compat = true;
    const prevInsert = cache.insert;
    let inserted: string[] = [];
    cache.insert = (...args) => {
      const serialized = args[1];
      if (cache.inserted[serialized.name] === undefined) {
        inserted.push(serialized.name);
      }
      return prevInsert(...args);
    };
    const flush = () => {
      const prevInserted = inserted;
      inserted = [];
      return prevInserted;
    };
    return { cache, flush };
  });

  useServerInsertedHTML(() => {
    const names = flush();
    if (names.length === 0) {
      return null;
    }
    let styles = "";
    for (const name of names) {
      styles += cache.inserted[name];
    }
    return (
      <style
        key={cache.key}
        data-emotion={`${cache.key} ${names.join(" ")}`}
        dangerouslySetInnerHTML={{
          __html: styles,
        }}
      />
    );
  });

  const experience = useActiveExperience();
  const theme = experience === "classic" ? classicTheme : modernTheme;

  return (
    <CacheProvider value={cache}>
      <CssVarsProvider theme={theme}>
        <ThemePreferenceSync />
        <CssBaseline />
        <GlobalStyles
          styles={(theme) => ({
            ":root": {
              "--Collapsed-breakpoint": "769px",
              "--Cover-width": "40vw",
              "--Form-maxWidth": "700px",
              "--Transition-duration": "0.4s",
              "--Header-height": "4rem",
              [theme.breakpoints.up("md")]: {
                "--Header-height": "0px",
              },
            },
          })}
        />
        {children}
      </CssVarsProvider>
    </CacheProvider>
  );
}

function ThemePreferenceSync() {
  useThemePreferenceSync();
  return null;
}
